import React, { memo, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { AppLocale } from '@procsea/common/utils'
import {
  Button,
  Collapsible,
  Flex,
  FormOnChange,
  Input,
  Stack,
  useBreakpoints,
} from '@procsea/design-system'

import InputLabel from './InputLabel'

export type TranslatedValue = { [lang in AppLocale]?: string | null }

export interface TranslatedInputProps {
  'data-e2e'?: string
  defaultLocale?: AppLocale
  disabled?: boolean
  errorMessage?: string
  fluid?: boolean
  helpText?: string
  label: string
  maxLength?: number
  name: string
  onChange: FormOnChange<TranslatedValue>
  required?: boolean
  value?: TranslatedValue
  warningMessage?: string
}

export const DEFAULT_TRANSLATED_INPUT_VALUE: TranslatedValue = {
  [AppLocale.DE]: null,
  [AppLocale.EN]: null,
  [AppLocale.FR]: null,
  [AppLocale.IT]: null,
}

const InputContainer = styled('div')<{ isMobile: boolean }>`
  display: ${props => (props.isMobile ? 'block' : 'flex')};
`
const FlexReverse = styled(Flex)`
  flex-wrap: wrap-reverse;
`

const StyledButton = styled(Button)<{ isMobile: boolean }>`
  & > :nth-child(2) {
    ${props => props.isMobile && 'padding-left: 0'};
  }
`

const getFilledInputCount = (value: TranslatedValue) =>
  Object.values(value).reduce((acc, translatedValue) => (translatedValue ? acc + 1 : acc), 0)

const TranslatedInput = ({
  'data-e2e': dataE2e,
  defaultLocale = AppLocale.EN,
  disabled,
  errorMessage,
  fluid,
  helpText,
  label,
  maxLength,
  name,
  onChange,
  required,
  value = DEFAULT_TRANSLATED_INPUT_VALUE,
  warningMessage,
}: TranslatedInputProps) => {
  const { isMobile } = useBreakpoints()
  const [expanded, setExpanded] = useState(false)
  const [filledInputCount, setFilledInputCount] = useState(getFilledInputCount(value))

  const totalInputsCount = Object.keys(value).length
  const { [defaultLocale]: visibleValue, ...expandableValues } = value

  useEffect(() => {
    setFilledInputCount(getFilledInputCount(value))
  }, [JSON.stringify(value)])

  const handleInputChange: FormOnChange<string> = useCallback(
    data => {
      const locale = data.name?.slice(-2) as AppLocale
      const newValue = { ...value, [locale]: data.value }

      setFilledInputCount(getFilledInputCount(newValue))
      onChange?.({ name, value: newValue })
    },
    [JSON.stringify(value), name]
  )
  return (
    <InputContainer data-e2e={dataE2e} isMobile={isMobile}>
      <Stack spacing="medium">
        <Stack>
          <FlexReverse>
            <InputLabel
              helpText={helpText}
              label={label}
              locale={defaultLocale}
              required={required}
            />

            <StyledButton
              data-e2e="expand-button"
              icon={expanded ? 'chevron-up' : 'chevron-down'}
              isMobile={isMobile}
              onClick={() => setExpanded(prevState => !prevState)}
              reversed
              text
              underline
              variant="primary"
            >
              {`${gettext('Manage translations')} (${filledInputCount}/${totalInputsCount})`}
            </StyledButton>
          </FlexReverse>

          <Input
            data-e2e={`${name}-${defaultLocale}`}
            disabled={disabled}
            errorMessage={errorMessage}
            fluid={fluid}
            maxLength={maxLength}
            name={`${name}-${defaultLocale}`}
            onChange={handleInputChange}
            value={visibleValue ?? ''}
            warningMessage={warningMessage}
            width={isMobile ? '100%' : '31em'}
          />
        </Stack>

        <Collapsible data-e2e="expand-content" open={expanded} setOpen={setExpanded}>
          <Stack spacing="medium">
            {Object.entries(expandableValues as TranslatedValue).map(
              ([locale, translatedValue]) => (
                <Stack key={locale}>
                  <InputLabel
                    helpText={helpText}
                    label={label}
                    locale={locale as AppLocale}
                    required={required}
                  />

                  <Input
                    data-e2e={`${name}-${locale}`}
                    disabled={disabled}
                    errorMessage={errorMessage}
                    fluid={fluid}
                    maxLength={maxLength}
                    name={`${name}-${locale}`}
                    onChange={handleInputChange}
                    value={translatedValue ?? ''}
                    warningMessage={warningMessage}
                    width={isMobile ? '100%' : '31em'}
                  />
                </Stack>
              )
            )}
          </Stack>
        </Collapsible>
      </Stack>
    </InputContainer>
  )
}

export default memo(TranslatedInput)
